/** @jsx isolateComponent */

import { FlexLayout, Modal } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { DateTime } from '@zapier/date';
import {
  Button,
  Heading,
  Link,
  Margin,
  Spacer,
  Text,
} from '@zapier/design-system';
import { PaymentApp } from '@zapier/payment';
import { Price } from '@zapier/currency';

import { useSize } from '@zapier/design-system-revenue/src/utils';

import { useData as _useData } from './useData';

type Props = {
  /** The amount Zapier will charge per month after the trial ends */
  monthlyAmount: number;
  /** The function called when the modal is closed */
  onClose: () => void;
  /** The function called when the continue button is clicked */
  onContinue: () => void;
  /** The date the trial ends */
  trialEndDate: number;
  /** The name of plan the modal is offering the trial for */
  trialPlanName: string;
  /** For dependency injection testing only */
  useData?: () => ReturnType<typeof _useData>;
};

export const TrialExtensionPaymentMethodModal = ({
  monthlyAmount,
  onClose,
  onContinue,
  trialEndDate,
  trialPlanName,
  useData = _useData,
}: Props) => {
  const { isPaymentMethodProvided, isPaymentSourceLoading } = useData();
  const size = useSize();

  return !isPaymentSourceLoading ? (
    <Modal
      eventName="revenue: viewed-trial-extension-modal"
      fullStoryEventName="Viewed Trial Extension Payment Method Modal"
      onClose={onClose}
    >
      <Heading>
        <Text type="header800">Start your 7-day premium trial</Text>
      </Heading>

      <Margin margin="20px 0">
        <Text tag="p" type="bodyText900">
          <strong>You won't be charged now.</strong> On{' '}
          <DateTime date={trialEndDate} />, you’ll be charged{' '}
          <Price>{monthlyAmount}</Price>/month for Zapier's{' '}
          <Link href="/app/billing/plans" target="_blank">
            {trialPlanName} plan
          </Link>
          . You won't be charged if you cancel your trial before it ends.
        </Text>
      </Margin>

      <PaymentApp />

      <Spacer height={20} />

      <FlexLayout
        direction={size === 'small' ? 'column' : 'row'}
        justify="flex-end"
      >
        <Button
          disabled={!isPaymentMethodProvided}
          hasShadow={true}
          isFullWidth={size === 'small'}
          onClick={onContinue}
        >
          Start trial
        </Button>
      </FlexLayout>
    </Modal>
  ) : null;
};
